import { useRef, useState } from 'react';
import { Upload, X, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import {
  useImportPreview,
  useImportCommit,
  useMe,
  type ImportOverlapSummary,
  type ImportPreview,
} from '../api/hooks.js';
import { toast } from '../store/toast.js';
import { useUiStore } from '../store/ui.js';
import { useUnsavedGuard } from '../lib/unsaved.js';

// Four states: idle (file picker) → previewed (review table) →
// [confirm-overwrite] → committed (close + toast). Cancel returns to
// idle, discarding the previewToken — the server will GC it after 10 min.
//
// The confirm-overwrite step only appears when the file lands on dates
// that already carry data. It is a separate screen with its own button,
// not a checkbox on the ordinary one: writing over punches somebody has
// already read and arbitrated is a different act from filling empty days,
// and it used to be the same click.

type ImportState =
  | { phase: 'idle' }
  | { phase: 'previewed'; preview: ImportPreview; previewToken: string }
  | { phase: 'confirm-overwrite'; preview: ImportPreview; previewToken: string };

interface Props {
  open: boolean;
  onClose: () => void;
  /** Scope the import to a specific entity. The server requires
   *  this; the parent page disables the trigger button when on
   *  the "All entities" filter so 'all' shouldn't reach here. */
  entityId: string;
}

export function ImportPointeuseModal({ open, onClose, entityId }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [state, setState] = useState<ImportState>({ phase: 'idle' });
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const previewMut = useImportPreview();
  const commitMut = useImportCommit();
  const me = useMe();
  const mayReimport = me.data?.permissions?.includes('attendance.reimport') ?? false;

  // Un aperçu ouvert vaut brouillon : le jeton d'aperçu ne survit pas à
  // un changement de société ou de page.
  useUnsavedGuard({
    id: 'import-pointeuse',
    dirty: open && state.phase !== 'idle',
    label: 'Import pointeuse',
    summary: 'aperçu en cours',
    discard: () => { reset(); onClose(); },
  });

  if (!open) return null;

  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const accepted = Array.from(newFiles).filter((f) =>
      /\.(xls|xlsx|csv)$/i.test(f.name),
    );
    setFiles((existing) => [...existing, ...accepted]);
  };

  const onParsePreview = async () => {
    if (files.length === 0) return;
    try {
      const res = await previewMut.mutateAsync({ files, entityId });
      setState({ phase: 'previewed', preview: res.preview, previewToken: res.previewToken });
    } catch (err) {
      const e = err as Error & {
        body?: { error?: string; message?: string };
        status?: number;
      };
      // Defense in depth — the trigger button is disabled when on
      // "All entities", so this should rarely fire. If it does
      // (e.g. user manipulated state), flash the entity bar to
      // direct attention.
      if (e.body?.error === 'entity_required') {
        useUiStore.getState().flashEntityBar();
      }
      toast.danger(e.body?.message ?? 'Échec de l\'analyse du fichier.');
    }
  };

  const runCommit = async (confirmOverwrite: boolean) => {
    if (state.phase === 'idle') return;
    try {
      const res = await commitMut.mutateAsync({ previewToken: state.previewToken, confirmOverwrite });
      const validatedNote = res.skippedValidated > 0 ? `, ${res.skippedValidated} refusés (jours validés)` : '';
      toast.success(
        `Import terminé : ${res.inserted} créés, ${res.updatedTimes} mis à jour, ${res.preservedStatus} préservés (statut manuel), ${res.skippedUnmatched} non rapprochés${validatedNote}.`,
      );
      // Un arbitrage annulé n'est pas un détail : ces personnes
      // retournent dans la file, il faut le voir maintenant. Champs
      // ajoutés après coup — une réponse qui ne les porte pas ne doit pas
      // faire tomber le toast de succès.
      const cleared = res.clearedDecisions ?? [];
      if (cleared.length > 0) {
        toast.danger(
          `${cleared.length} arbitrage(s) annulé(s) — les pointages ont changé : ${cleared
            .slice(0, 3)
            .map((c) => `${c.employeeName} (${c.date})`)
            .join(', ')}${cleared.length > 3 ? '…' : ''}. À arbitrer de nouveau.`,
        );
      }
      if ((res.staleGridCells ?? 0) > 0) {
        toast.danger(
          `${res.staleGridCells} décision(s) paie de la grille mensuelle reposent sur des pointages qui viennent de changer — à revoir.`,
        );
      }
      reset();
      onClose();
    } catch (err) {
      const e = err as Error & { body?: { error?: string; message?: string } };
      toast.danger(e.body?.message ?? 'Échec du commit.');
    }
  };

  // Ordinary path: virgin dates commit straight away; anything else
  // detours through the explicit overwrite screen.
  const onCommit = () => {
    if (state.phase !== 'previewed') return;
    if (state.preview.overlap) {
      setState({ phase: 'confirm-overwrite', preview: state.preview, previewToken: state.previewToken });
      return;
    }
    void runCommit(false);
  };

  const onCancel = () => {
    reset();
    onClose();
  };

  const reset = () => {
    setFiles([]);
    setState({ phase: 'idle' });
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="modal-card modal-card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-title"
        onClick={(e) => e.stopPropagation()}
        data-testid="import-modal"
      >
        <h2 id="import-title">Importer le pointage</h2>

        {state.phase === 'idle' ? (
          <>
            <p>Glissez les fichiers du badgeage (.xls, .xlsx ou .csv) ou cliquez pour les sélectionner.</p>
            <div
              className={`dropzone${dragging ? ' is-dragging' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                addFiles(e.dataTransfer.files);
              }}
              onClick={() => inputRef.current?.click()}
              data-testid="import-dropzone"
            >
              <Upload aria-hidden="true" />
              <p>Déposer ici ou cliquer pour parcourir</p>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".xls,.xlsx,.csv"
                onChange={(e) => addFiles(e.target.files)}
                hidden
                data-testid="import-file-input"
              />
            </div>

            {files.length > 0 && (
              <ul className="file-list" data-testid="import-file-list">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    <span>{f.name}</span>
                    <span className="muted mono">{Math.round(f.size / 1024)} KB</span>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))}
                      aria-label={`Remove ${f.name}`}
                    >
                      <X aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="modal-actions">
              <button type="button" className="btn" onClick={onCancel}>
                Annuler
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={files.length === 0 || previewMut.isPending}
                onClick={onParsePreview}
                data-testid="import-parse-preview"
              >
                {previewMut.isPending ? 'Analyse…' : 'Analyser'}
              </button>
            </div>
          </>
        ) : state.phase === 'confirm-overwrite' ? (
          <OverwriteConfirm
            overlap={state.preview.overlap!}
            mayReimport={mayReimport}
            committing={commitMut.isPending}
            onBack={() =>
              setState({ phase: 'previewed', preview: state.preview, previewToken: state.previewToken })
            }
            onConfirm={() => void runCommit(true)}
          />
        ) : (
          <ImportPreviewReview
            preview={state.preview}
            mayReimport={mayReimport}
            onCancel={onCancel}
            onCommit={onCommit}
            committing={commitMut.isPending}
          />
        )}
      </div>
    </div>
  );
}

// The overwrite gate. Deliberately a full screen with its own button:
// « Enregistrer » fills empty days, « Ré-importer et écraser » overwrites
// what a human already read. Without the permission it is a dead end with
// a plain explanation, not a disabled mystery.
function OverwriteConfirm({
  overlap,
  mayReimport,
  committing,
  onBack,
  onConfirm,
}: {
  overlap: ImportOverlapSummary;
  mayReimport: boolean;
  committing: boolean;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const dates = overlap.dates;
  const dateLabel =
    dates.length === 0
      ? '—'
      : dates.length === 1
        ? dates[0]!
        : `${dates[0]} → ${dates[dates.length - 1]} (${dates.length} jours)`;

  return (
    <div data-testid="import-overwrite-confirm">
      <div className="preview-unmatched" style={{ borderColor: 'var(--warn)' }}>
        <h3>
          <ShieldAlert aria-hidden="true" /> Ces dates ont déjà un pointage
        </h3>
        <p>
          {mayReimport
            ? "L'import ne remplit pas des journées vides : il écrase des données déjà saisies, lues et parfois arbitrées."
            : 'Ces dates portent déjà des données. Seul un administrateur peut les ré-importer — demandez-lui de le faire.'}
        </p>
        <ul className="muted">
          <li>
            <strong>{overlap.rows}</strong> ligne{overlap.rows > 1 ? 's' : ''} écrasée
            {overlap.rows > 1 ? 's' : ''} sur <strong>{overlap.employees}</strong> personne
            {overlap.employees > 1 ? 's' : ''}
          </li>
          <li>Période concernée : <strong>{dateLabel}</strong></li>
          {overlap.arbitrated > 0 && (
            <li>
              <strong>{overlap.arbitrated}</strong> ligne{overlap.arbitrated > 1 ? 's' : ''} déjà arbitrée
              {overlap.arbitrated > 1 ? 's' : ''} — l'arbitrage sera annulé et à reprendre
            </li>
          )}
          {overlap.validated > 0 && (
            <li>
              <strong>{overlap.validated}</strong> ligne{overlap.validated > 1 ? 's' : ''} sur journée validée —
              refusée{overlap.validated > 1 ? 's' : ''} : rouvrez la journée pour la corriger
            </li>
          )}
          {overlap.paieCells > 0 && (
            <li>
              <strong>{overlap.paieCells}</strong> décision{overlap.paieCells > 1 ? 's' : ''} paie de la grille
              mensuelle repose{overlap.paieCells > 1 ? 'nt' : ''} sur ces pointages — à revoir ensuite
            </li>
          )}
          {(overlap.corrected ?? 0) > 0 && (
            <li data-testid="import-overwrite-corrected">
              <strong>{overlap.corrected}</strong> ligne{overlap.corrected! > 1 ? 's' : ''} avec une heure corrigée à
              la main — la correction est <strong>conservée</strong>, l'heure pointeuse reste visible à côté
            </li>
          )}
        </ul>
      </div>

      <div className="modal-actions">
        <button type="button" className="btn" onClick={onBack} data-testid="import-overwrite-back">
          Revenir à l'aperçu
        </button>
        {mayReimport && (
          <button
            type="button"
            className="btn btn--danger"
            onClick={onConfirm}
            disabled={committing}
            data-testid="import-overwrite-confirm-btn"
          >
            {committing ? 'Écrasement…' : 'Ré-importer et écraser'}
          </button>
        )}
      </div>
    </div>
  );
}

function ImportPreviewReview({
  preview,
  mayReimport,
  onCancel,
  onCommit,
  committing,
}: {
  preview: ImportPreview;
  mayReimport: boolean;
  onCancel: () => void;
  onCommit: () => void;
  committing: boolean;
}) {
  const counts = preview.totals;
  const preservedCount = preview.matchedRecords.filter((r) => r.action === 'preserve-status').length;
  const updateCount = preview.matchedRecords.filter((r) => r.action === 'update-times').length;
  const createCount = preview.matchedRecords.filter((r) => r.action === 'create').length;
  const validatedCount = counts.validatedConflicts ?? 0;
  const distinctEmployees = counts.distinctEmployees ?? 0;
  const overlap = preview.overlap;
  // Validated days are skipped on commit, so they don't count toward
  // what will actually be written.
  const writableCount = preview.matchedRecords.length - validatedCount;

  return (
    <>
      <div className="preview-summary" data-testid="import-preview-summary">
        <div className="preview-summary-row">
          <CheckCircle2 aria-hidden="true" />
          <span>
            <strong>{counts.distinctDates}</strong> jours · <strong>{distinctEmployees}</strong> employés · <strong>{createCount}</strong> nouveaux / <strong>{updateCount}</strong> mis à jour · <strong>{counts.rowsUnmatched}</strong> inconnus ignorés · <strong>{validatedCount}</strong> conflits sur jours validés
          </span>
        </div>
        <div className="preview-summary-row">
          <span>
            Période : <strong>{preview.dateRange?.from ?? '—'}</strong> → <strong>{preview.dateRange?.to ?? '—'}</strong> · <strong>{counts.rowsParsed}</strong> lignes analysées · <strong>{preservedCount}</strong> statuts manuels préservés
          </span>
        </div>
      </div>

      {overlap && (
        <div className="preview-unmatched" data-testid="import-overlap-warning" style={{ borderColor: 'var(--warn)' }}>
          <h3>
            <ShieldAlert aria-hidden="true" /> {overlap.rows} ligne{overlap.rows > 1 ? 's' : ''} sur des dates déjà
            renseignées
          </h3>
          <p>
            {mayReimport
              ? "Ce n'est pas un premier import : une confirmation d'écrasement sera demandée."
              : 'Ces dates ont déjà un pointage — seul un administrateur peut ré-importer.'}
          </p>
        </div>
      )}

      {validatedCount > 0 && (
        <div className="preview-unmatched" data-testid="import-validated-conflicts">
          <h3>
            <AlertTriangle aria-hidden="true" /> {validatedCount} jours déjà validés
          </h3>
          <p>
            Ces journées ont été signées dans la Saisie quotidienne : leurs horaires ne seront
            pas modifiés par l'import — elles seront listées comme refusées dans le rapport.
            Rouvrez la journée si vous devez la corriger.
          </p>
        </div>
      )}

      {preview.pointageExcludedNames.length > 0 && (
        <div className="preview-unmatched" data-testid="import-pointage-excluded">
          <h3>
            <AlertTriangle aria-hidden="true" /> {preview.pointageExcludedNames.length} hors pointage — lignes ignorées
          </h3>
          <p>
            Ces salariés sont marqués « hors pointage » sur leur fiche : leurs lignes du fichier ne sont pas
            importées. Rien à corriger — pour les réintégrer, retirez le marquage depuis leur fiche.
          </p>
          <ul className="muted">
            {preview.pointageExcludedNames.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}

      {preview.unmatchedNames.length > 0 && (
        <div className="preview-unmatched" data-testid="import-unmatched">
          <h3>
            <AlertTriangle aria-hidden="true" /> {preview.unmatchedNames.length} noms non reconnus
          </h3>
          <p>
            Ces noms ne correspondent à aucun salarié actif. Pour les importer, créez d'abord les salariés depuis l'onglet
            Employés, puis relancez l'import. Les autres lignes seront enregistrées normalement.
          </p>
          <ul className="muted">
            {preview.unmatchedNames.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}

      {preview.matchedRecords.length > 0 && (
        <div className="preview-matched">
          <h3>Aperçu des lignes ({preview.matchedRecords.length})</h3>
          <div className="preview-table-wrap">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Salarié</th>
                  <th>Date</th>
                  <th>Entrée</th>
                  <th>Sortie</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {preview.matchedRecords.slice(0, 200).map((r, i) => (
                  <tr key={`${r.employeeId}-${r.date}-${i}`}>
                    <td>{r.employeeName}</td>
                    <td className="mono">{r.date}</td>
                    <td className="mono">{r.checkIn ?? '—'}</td>
                    <td className="mono">{r.checkOut ?? '—'}</td>
                    <td>{actionLabel(r.action, r.existingStatus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.matchedRecords.length > 200 && (
              <p className="muted">… et {preview.matchedRecords.length - 200} de plus. Toutes seront enregistrées.</p>
            )}
          </div>
        </div>
      )}

      <div className="modal-actions">
        <button type="button" className="btn" onClick={onCancel}>
          Annuler
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={onCommit}
          disabled={committing || writableCount === 0 || (overlap != null && !mayReimport)}
          title={overlap != null && !mayReimport ? 'Ces dates ont déjà un pointage — seul un administrateur peut ré-importer.' : undefined}
          data-testid="import-commit"
        >
          {committing
            ? 'Enregistrement…'
            : overlap
              ? `Continuer (${writableCount} lignes)`
              : `Enregistrer (${writableCount} lignes)`}
        </button>
      </div>
    </>
  );
}

function actionLabel(action: string, existing?: string): string {
  switch (action) {
    case 'create':
      return 'Création';
    case 'update-times':
      return 'Mise à jour des horaires';
    case 'preserve-status':
      return `Préserve statut${existing ? ` (${existing})` : ''}`;
    case 'skip-validated':
      return 'Refusé · jour validé';
    default:
      return action;
  }
}
